import { Alert, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { manifestFieldBox } from "components/Document/ReviewDocumentMetadataDialog/components/manifestFieldBoxes";
import { getFieldLabel } from "encamp-shared/src/waste/discrepancyFieldMeta";
import {
  EMANIFEST_WASTE_UNIT_LABELS,
  normalizeEpaWasteUnit,
} from "encamp-shared/src/waste/eManifestWasteUnit";
import { invalidManifestCorrectionWasteCodes } from "encamp-shared/src/waste/manifestCorrectionWasteCodes";
import { useState } from "react";
import { formatFieldValue } from "routes/Customer/Waste/Manifests/discrepancyValueFormat";
import {
  computeManualValue,
  isWasteCodeField,
  manualValueError,
} from "routes/Customer/Waste/Manifests/resolveWithValue.helpers";
import { WasteCodeChipInput } from "routes/Customer/Waste/Submissions/Stepper/steps/WasteCodeChipInput";

import { CorrectionFieldDraft } from "./correctionDraft";

type Props = {
  field: string;
  name?: string;
  lineId: string | null;
  group: string;
  value?: string | number | string[] | null;
  documentQuantity?: string;
  existing?: CorrectionFieldDraft;
  processedManifestId: string;
  onSave: (correction: CorrectionFieldDraft) => void;
  onCancel: () => void;
};

function originalValueLabel(field: string, value: Props["value"]): string {
  if (Array.isArray(value)) return value.join(", ") || "Blank";
  return formatFieldValue(field, value == null ? null : String(value));
}

export function CorrectionValueEditor({
  field,
  name,
  lineId,
  group,
  value,
  documentQuantity,
  existing,
  processedManifestId,
  onSave,
  onCancel,
}: Props) {
  const [text, setText] = useState(existing?.correctValue ?? "");
  const [codes, setCodes] = useState(existing?.codes ?? []);
  const [pendingCode, setPendingCode] = useState("");
  const draftCodes = pendingCode.trim() ? [...codes, pendingCode] : codes;
  const correctValue = computeManualValue(field, text, draftCodes);
  const error = manualValueError(field, correctValue);
  const changed =
    !existing || correctValue !== computeManualValue(field, existing.correctValue, existing.codes);
  const canSave = error == null && changed;
  const unitField = field === "quantityUnitOfMeasureCode";
  const label = getFieldLabel(field);
  const save = () => {
    if (!canSave) return;
    onSave({
      field,
      target: { processedManifestId, wasteLineUnifiedId: lineId },
      selectionLabel: `${group} · ${label}`,
      correctValue,
      codes: draftCodes,
    });
  };
  return (
    <Stack
      component="form"
      spacing={1}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
    >
      {isWasteCodeField(field) ? (
        <WasteCodeChipInput
          name={name}
          label="Should be"
          codes={codes}
          onChange={setCodes}
          onInputChange={setPendingCode}
          size="small"
          invalidCodes={error ? invalidManifestCorrectionWasteCodes(field, draftCodes) : []}
          error={draftCodes.length > 0 && error != null}
          helperText={draftCodes.length > 0 ? (error ?? undefined) : undefined}
        />
      ) : (
        <TextField
          name={name}
          select={unitField}
          label={field === "quantity" ? "Should be (lbs)" : "Should be"}
          value={unitField ? (normalizeEpaWasteUnit(text) ?? "") : text}
          onChange={(event) => setText(event.target.value)}
          error={text.trim() !== "" && error != null}
          helperText={text.trim() !== "" ? error : undefined}
          size="small"
          fullWidth
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { "aria-label": `${label} — ${group}` },
            select: { SelectDisplayProps: { "aria-label": `${label} — ${group}` } },
          }}
        >
          {unitField &&
            Object.entries(EMANIFEST_WASTE_UNIT_LABELS)
              .filter(([code]) => normalizeEpaWasteUnit(code))
              .map(([code, unitLabel]) => (
                <MenuItem key={code} value={code}>
                  {code} — {unitLabel}
                </MenuItem>
              ))}
        </TextField>
      )}
      <CorrectionDocumentReference
        field={field}
        name={name}
        value={value}
        documentQuantity={documentQuantity}
      />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button aria-label="Cancel correction" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!canSave}>
          {existing ? "Update correction" : "Add correction"}
        </Button>
      </Stack>
    </Stack>
  );
}

function CorrectionDocumentReference({
  field,
  name,
  value,
  documentQuantity,
}: Pick<Props, "field" | "name" | "value" | "documentQuantity">) {
  const box = name ? manifestFieldBox(name)?.box : undefined;
  const original = originalValueLabel(field, value);
  return (
    <Stack spacing={1}>
      {!name && (
        <Alert severity="info">
          No matching document row was found. Check the manifest manually before recording a
          correction.
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        {documentQuantity
          ? `Document: ${documentQuantity} · Original: ${original}`
          : `Original: ${original}`}
        {box ? ` · Box ${box}` : ""}
      </Typography>
    </Stack>
  );
}
